<template>
    <div>
      <InventoryHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Bulk Product Upload</h5>
          <p class="page-sub">Import multiple products at once using a spreadsheet template</p>
        </div>
        <button type="button" @click="$router.go(-1)" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
  
      <div class="upload-layout">
  
        <!-- LEFT: Upload Steps -->
        <div>
  
          <!-- Step 1 -->
          <div class="form-card mb-4">
            <div class="section-header">
              <div class="step-badge">1</div>
              <div>
                <p class="section-title">Download Template</p>
                <p class="section-sub">Use our template to ensure correct formatting</p>
              </div>
            </div>
            <button
              class="btn-primary-action w-full"
              type="button"
              @click="downloadFile('/inventory/product/download/upload', 'Product_Upload_Template.xlsx')"
              :disabled="downloading"
            >
              <span v-if="downloading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <font-awesome-icon v-else icon="download" class="me-2" />
              {{ downloading ? 'Downloading...' : 'Download Template' }}
            </button>
            <a
              href="#"
              class="sample-link"
              @click.prevent="downloadFile('/inventory/product/download/sample', 'Product_Sample_Template.xlsx')"
            >
              <font-awesome-icon icon="file-excel" class="me-1" /> Download Sample File instead
            </a>
          </div>
  
          <!-- Step 2 -->
          <div class="form-card">
            <div class="section-header">
              <div class="step-badge">2</div>
              <div>
                <p class="section-title">Select & Upload File</p>
                <p class="section-sub">Choose your completed spreadsheet and upload</p>
              </div>
            </div>
  
            <!-- Dropzone — clicking the styled div triggers the real hidden input via ref -->
            <div class="dropzone-wrap" @click="triggerFileInput">
              <input
                type="file"
                ref="fileInputRef"
                accept=".xlsx,.xls,.csv"
                class="dropzone-hidden-input"
                @change="onFileChange"
              />
              <div v-if="!import_file_name" class="dropzone-empty">
                <div class="dropzone-icon">
                  <font-awesome-icon icon="file-upload" />
                </div>
                <p class="dropzone-label">Click to browse or drag your file here</p>
                <p class="dropzone-hint">Supports .xlsx, .xls, .csv</p>
              </div>
              <div v-else class="dropzone-selected">
                <div class="file-icon">
                  <font-awesome-icon icon="file-excel" />
                </div>
                <div class="file-meta">
                  <p class="file-name">{{ import_file_name }}</p>
                  <p class="file-change">Click to change file</p>
                </div>
                <font-awesome-icon icon="check-circle" class="file-check-icon" />
              </div>
            </div>
  
            <!-- Validation Errors -->
            <div v-if="validationErrors?.length || dateFormatError" class="error-banner">
              <font-awesome-icon icon="exclamation-circle" class="error-icon" />
              <div>
                <p v-for="(error, index) in validationErrors" :key="index" class="mb-0">{{ error[0] }}</p>
                <p v-if="dateFormatError" class="mb-0">{{ dateFormatError }}</p>
              </div>
            </div>
  
            <!-- Upload Button — only disabled while loading, never disabled based on file selection -->
            <button
              class="btn-primary-action w-full"
              type="button"
              @click="importproducts"
              :disabled="loading"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <font-awesome-icon v-else icon="upload" class="me-2" />
              {{ loading ? 'Uploading...' : 'Upload Products' }}
            </button>
          </div>
  
        </div>
  
        <!-- RIGHT: Instructions -->
        <div class="form-card">
          <div class="section-header">
            <div class="section-icon">
              <font-awesome-icon icon="info-circle" />
            </div>
            <div>
              <p class="section-title">Instructions</p>
              <p class="section-sub">Follow these steps carefully to avoid import errors</p>
            </div>
          </div>
  
          <div class="instruction-list">
            <div v-for="(item, i) in instructions" :key="i" class="instruction-row">
              <span class="instruction-num">{{ i + 1 }}</span>
              <p class="instruction-text">
                <strong>{{ item.label }}</strong>
                <span v-if="item.text"> — {{ item.text }}</span>
              </p>
            </div>
          </div>
  
          <div class="notes-box">
            <p class="notes-title">
              <font-awesome-icon icon="exclamation-triangle" class="me-2" />Important Notes
            </p>
            <ul class="notes-list">
              <li>Save quantity in <strong>pieces</strong> when using variants</li>
              <li>Store name must already exist in <strong>Administration → Manage Stores</strong></li>
              <li>Columns marked <strong>"TEXT"</strong> must have their Excel cells formatted as Text before entering data</li>
            </ul>
          </div>
        </div>
  
      </div>
    </div>
  </template>
  
  <script>
  import InventoryHeaderTab from './component/inventoryheadertab.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useRouter } from 'vue-router';
  import { ref } from '@vue/reactivity';
  import { useReusable } from '../../../lib/reusable';
  
  export default {
    components: { InventoryHeaderTab },
  
    setup() {
      const { downloading, downloadFile } = useReusable();
      const fileInputRef     = ref(null);
      const import_file      = ref(null);
      const import_file_name = ref('');
      const loading          = ref(false);
      const toast            = useToast();
      const router           = useRouter();
      const validationErrors = ref([]);
      const dateFormatError  = ref('');
  
      // Programmatically open the hidden file input
      const triggerFileInput = () => {
        fileInputRef.value?.click();
      };
  
      const onFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        import_file.value      = file;
        import_file_name.value = file.name;
      };
  
      const importproducts = () => {
        if (!import_file.value) {
          toast.warning('Please select a file first');
          return;
        }
        validationErrors.value = [];
        dateFormatError.value  = '';
        loading.value          = true;
  
        const formData = new FormData();
        formData.append('import_file', import_file.value);
  
        axios.post('/inventory/product/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }).then(response => {
          loading.value = false;
          toast.success(response.data.message);
          router.push('/all-products');
        }).catch(error => {
          loading.value = false;
          if (error?.response?.status == 422) {
            validationErrors.value = error.response.data.errors;
            toast.warning(error.response.data.message);
          }
          if (error?.response?.status == 500) toast.error('An error occurred, please try again');
          if (error?.response?.status == 400) {
            dateFormatError.value = error.response.data.message;
            toast.error('There is an error in your input');
          }
          if (error.message === 'Network Error') {
            import_file.value      = null;
            import_file_name.value = '';
            toast.error('Network error — please select your file again');
          }
        });
      };
  
      const instructions = [
        { label: 'Download Template', text: "Use the 'Download Template' button above to get the correct spreadsheet format." },
        { label: 'Product Name', text: 'Name of the inventory item. Compulsory.' },
        { label: 'Barcode', text: 'Not mandatory. Ensure the Excel column is set to TEXT format.' },
        { label: 'Unit of Measurement', text: 'e.g. Pcs, Carton, Dozen, Kg. Compulsory.' },
        { label: 'Category', text: 'Not mandatory but required for category reports. Auto-created if it does not exist.' },
        { label: 'Cost Price', text: 'Use 0 if not needed for profitability reports.' },
        { label: 'Selling Price', text: 'Use 0 if not needed for profitability reports.' },
        { label: 'Store Name', text: 'Must match exactly (case-sensitive) the store in Administration → Manage Stores. Compulsory.' },
        { label: 'Quantity', text: 'Use 0 if you do not want to enter a quantity.' },
        { label: 'Best Before', text: 'Format: DD/MM/YY (e.g. 01/01/30 for 1 Jan 2030). Years 00–68 → 2000–2068, 69–99 → 1969–1999. Ensure the Excel column is set to TEXT format.' },
        { label: 'Tax', text: 'Exact tax name(s) from Settings → Tax. Separate multiple with commas. Column must be TEXT format.' },
        { label: 'Warning Quantity', text: 'Notifies you when stock falls to this level. Use 0 to disable.' },
        { label: 'Variant Name', text: 'Names for product fractions (e.g. Pieces, Carton). Separate with commas. Column must be TEXT format.' },
        { label: 'Variant Qty', text: 'Units per variant — same order as Variant Names. Separate with commas. Column must be TEXT format.' },
        { label: 'Variant Price', text: 'Price per variant — same order as Variant Names. Separate with commas. Column must be TEXT format.' },
        { label: 'Save & Upload', text: "Save the completed file, select it in Step 2 above, then click 'Upload Products'." },
      ];
  
      return {
        loading, downloading, validationErrors, fileInputRef,
        import_file, import_file_name, dateFormatError, instructions,
        onFileChange, importproducts, downloadFile, triggerFileInput,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Layout ── */
  .upload-layout { display:grid; grid-template-columns:360px 1fr; gap:24px; align-items:start; }
  
  /* ── Form Card ── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4 { margin-bottom:16px; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .section-icon   { width:42px; height:42px; border-radius:10px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Step Badge ── */
  .step-badge { width:36px; height:36px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:15px; font-weight:700; flex-shrink:0; }
  
  /* ── Sample Link ── */
  .sample-link { display:flex; align-items:center; justify-content:center; gap:5px; font-size:13px; color:#004AAD; text-decoration:none; margin-top:10px; }
  .sample-link:hover { text-decoration:underline; }
  
  /* ── Dropzone ── */
  .dropzone-wrap {
    border:2px dashed #dde5f0; border-radius:12px; background:#f8fafc;
    cursor:pointer; transition:border-color .15s, background .15s;
    position:relative; margin-bottom:12px;
  }
  .dropzone-wrap:hover { border-color:#004AAD; background:#f0f5ff; }
  
  .dropzone-hidden-input {
    position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; z-index:-1;
  }
  
  .dropzone-empty {
    display:flex; flex-direction:column; align-items:center; justify-content:center;
    padding:40px 20px; text-align:center;
  }
  .dropzone-icon  { width:52px; height:52px; border-radius:12px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:22px; margin-bottom:12px; }
  .dropzone-label { font-size:14px; font-weight:600; color:#1a2b45; margin:0 0 4px; }
  .dropzone-hint  { font-size:12px; color:#aab8cc; margin:0; }
  
  .dropzone-selected { display:flex; align-items:center; gap:14px; padding:16px 20px; }
  .file-icon       { width:42px; height:42px; border-radius:10px; background:rgba(22,163,74,.1); color:#16a34a; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
  .file-meta       { flex:1; min-width:0; }
  .file-name       { font-size:13.5px; font-weight:600; color:#1a2b45; margin:0 0 2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .file-change     { font-size:12px; color:#7a90b0; margin:0; }
  .file-check-icon { font-size:20px; color:#16a34a; flex-shrink:0; }
  
  /* ── Error Banner ── */
  .error-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; color:#e03535; font-size:13px; margin-bottom:12px; }
  .error-icon   { font-size:16px; margin-top:2px; flex-shrink:0; }
  
  /* ── Buttons ── */
  .btn-primary-action {
    display:inline-flex; align-items:center; justify-content:center;
    background:#004AAD; color:#fff; font-size:13px; font-weight:600;
    padding:10px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s;
  }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .w-full { width:100%; }
  
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  
  /* ── Instructions ── */
  .instruction-list { display:flex; flex-direction:column; gap:10px; margin-bottom:20px; }
  .instruction-row  { display:flex; align-items:flex-start; gap:10px; }
  .instruction-num  { display:inline-flex; align-items:center; justify-content:center; min-width:24px; height:24px; border-radius:50%; background:#edf4ff; color:#004AAD; font-size:11px; font-weight:700; flex-shrink:0; margin-top:2px; }
  .instruction-text { font-size:13px; color:#4a5e78; line-height:1.6; margin:0; }
  .instruction-text strong { color:#1a2b45; }
  
  /* ── Notes ── */
  .notes-box   { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:14px 16px; }
  .notes-title { font-size:13px; font-weight:700; color:#e08c00; margin:0 0 10px; }
  .notes-list  { margin:0; padding-left:18px; }
  .notes-list li { font-size:13px; color:#4a5e78; margin-bottom:6px; line-height:1.5; }
  .notes-list li:last-child { margin-bottom:0; }
  
  /* ── Responsive ── */
  @media(max-width:1024px) { .upload-layout { grid-template-columns:1fr; } }
  @media(max-width:767px) {
    .page-header { flex-direction:column; align-items:stretch; }
    .btn-outline { width:100%; justify-content:center; }
  }
  </style>